import type { AccountTransferState, HouseholdFundingPhase } from '@savvy/constants/generated/gql-enums';

import type { ClientPortalTransferAdvisorTeamMember } from '../api/transfer-advisor-query';

const PORTAL_ADMITTED_PHASES: readonly HouseholdFundingPhase[] = [
  'NO_ACTIVE_ACCOUNTS',
  'TRANSFERRING',
  'PARTIALLY_FUNDED',
  'FUNDED',
];

const TRANSFER_TREATMENT_PHASES: readonly HouseholdFundingPhase[] = ['AWAITING_ACCOUNT_OPENING', 'TRANSFERRING'];

export function admitsRedesignedPortal(
  phase: HouseholdFundingPhase | null | undefined,
  hasIncompleteOnboardingSignature: boolean,
) {
  if (phase === null || phase === undefined) {
    return false;
  }
  if (phase === 'AWAITING_ACCOUNT_OPENING') {
    return !hasIncompleteOnboardingSignature;
  }
  return PORTAL_ADMITTED_PHASES.includes(phase);
}

export function isTransferringPhase(phase: HouseholdFundingPhase | null | undefined) {
  return phase !== null && phase !== undefined && TRANSFER_TREATMENT_PHASES.includes(phase);
}

export const TRANSFER_TIMELINE_IN_REVIEW_STEP = 1 as const;

export const TRANSFER_TIMELINE_ASSETS_MOVING_STEP = 2 as const;

export type TransferTimelineStep =
  | typeof TRANSFER_TIMELINE_IN_REVIEW_STEP
  | typeof TRANSFER_TIMELINE_ASSETS_MOVING_STEP;

type AccountTransferProgress = {
  readonly transferState: AccountTransferState;
  readonly terminal: boolean;
};

const ASSETS_LEFT_THE_OLD_FIRM: Record<AccountTransferState, boolean> = {
  PENDING: false,
  SENT_TO_CUSTODIAN: true,
  TRANSFERRED: true,
  CANCELLED: false,
  UNKNOWN: false,
};

export function transferTimelineStep(accounts: readonly AccountTransferProgress[]): TransferTimelineStep {
  const moving = accounts
    .filter((account) => !account.terminal)
    .some((account) => ASSETS_LEFT_THE_OLD_FIRM[account.transferState]);

  return moving ? TRANSFER_TIMELINE_ASSETS_MOVING_STEP : TRANSFER_TIMELINE_IN_REVIEW_STEP;
}

export function transferDestinationCustodian(
  accounts: readonly { readonly newAccountCustodian: string | null }[],
): string | null {
  const destinations = new Set(
    accounts
      .map((account) => (account.newAccountCustodian ?? '').trim().toLowerCase())
      .filter((destination) => destination !== ''),
  );

  return destinations.size === 1 ? (destinations.values().next().value ?? null) : null;
}

export type ClientPortalFundingPhaseState = {
  readonly phase: HouseholdFundingPhase | null;
  readonly destinationCustodian: string | null;
  readonly loading: boolean;
  readonly admitted: boolean;
  readonly transferring: boolean;
  readonly transferTimelineStep: TransferTimelineStep;
};

type FundingPhaseStateInput = {
  readonly isClient: boolean;
  readonly canQuery: boolean;
  readonly queryLoading: boolean;
  readonly phase: HouseholdFundingPhase | null;
  readonly clientActionsComplete?: boolean;
  readonly hasIncompleteOnboardingSignature?: boolean;
  readonly clientActionsLoading?: boolean;
  readonly destinationCustodian?: string | null;
  readonly accounts?: readonly AccountTransferProgress[];
};

export function fundingPhaseState({
  isClient,
  canQuery,
  queryLoading,
  phase,
  clientActionsComplete = false,
  hasIncompleteOnboardingSignature = false,
  clientActionsLoading = false,
  destinationCustodian = null,
  accounts = [],
}: FundingPhaseStateInput): ClientPortalFundingPhaseState {
  return {
    phase,
    destinationCustodian,
    loading: isClient && (!canQuery || queryLoading || clientActionsLoading),
    admitted: admitsRedesignedPortal(phase, hasIncompleteOnboardingSignature),
    transferring: phase === 'TRANSFERRING' || (phase === 'AWAITING_ACCOUNT_OPENING' && clientActionsComplete),
    transferTimelineStep: transferTimelineStep(accounts),
  };
}

export type TransferAdvisorContact = {
  readonly name: string;
  readonly email: string;
};

export function transferAdvisorContact(
  team: readonly ClientPortalTransferAdvisorTeamMember[],
  leadAdvisorId: string | null | undefined,
): TransferAdvisorContact | undefined {
  const lead = team.find((member) => member.id === leadAdvisorId) ?? team.at(0);
  const name = [lead?.user.firstName, lead?.user.lastName].filter((part) => (part ?? '') !== '').join(' ');
  const email = lead?.displayEmail ?? '';

  if (name === '' || email === '') {
    return undefined;
  }
  return { name, email };
}
